// Counts every number in an element's text up to its value when the element scrolls into view,
// once - starting close to it, so it reads as a nudge rather than a jump: from START_FRACTION of
// the value, but never more than MAX_STEPS steps below it (35 counts 28 -> 35, 2000 counts
// 1960 -> 2000 rather than 1600 -> 2000; a step is 1, or 0.1 for "5.2") -
// all at the same time, so a range like "2009–2013" counts both ends. Opt in with data-count-up on
// the element (e.g. Block Text Items' large text: "4", "35", "SEK 5bn+"). Everything around the
// numbers ("SEK ", "bn+", "–") stays as authored.
//
// Progressive enhancement: the server renders the final text, so no-JS and reduced-motion visitors
// see the real value as is. With JS, the numbers are set to their start value on init and count
// up to the real value the first time the element is 40% in view.
//
// Number formats handled: integers ("35"), decimals ("2.5" / "2,5") and thousands groups
// ("1 200", "1,200", "12.000") - each is formatted back with the separators it was authored with.

const DURATION_MS = 1500; // Unverified - no Figma motion spec for this.
const THRESHOLD = 0.4; // Same default as scroll-reveal.ts.
const START_FRACTION = 0.8; // Unverified - our own pick.
const MAX_STEPS = 40; // Unverified - our own pick.

function startValue(parsed: ParsedNumber): number {
    const step = 10 ** -parsed.decimals;
    return Math.max(parsed.value * START_FRACTION, parsed.value - MAX_STEPS * step);
}

// Groups of exactly three digits after a separator are thousands; anything else after . or , is
// decimals. "1,200" is read as twelve hundred, "1,2" as one point two. A leading 0 is never a
// thousands group, so "0.125" stays a decimal.
export const NUMBER_PATTERN = /[1-9]\d{0,2}(?:([   .,])\d{3})+(?:([.,])(\d+))?|\d+(?:([.,])(\d+))?/;

export interface ParsedNumber {
    value: number;
    decimals: number;
    groupSeparator: string | null;
    decimalSeparator: string;
}

export function parseNumber(match: RegExpExecArray): ParsedNumber {
    const groupSeparator = match[1] ?? null;
    const decimalSeparator = match[2] ?? match[4] ?? ".";
    const decimalDigits = match[3] ?? match[5] ?? "";
    const integerDigits = match[0].slice(0, match[0].length - (decimalDigits ? decimalDigits.length + 1 : 0)).replace(/\D/g, "");

    return {
        value: Number.parseFloat(`${integerDigits}.${decimalDigits || "0"}`),
        decimals: decimalDigits.length,
        groupSeparator,
        decimalSeparator,
    };
}

export function formatNumber(value: number, parsed: ParsedNumber): string {
    const [integerPart = "", decimalPart] = value.toFixed(parsed.decimals).split(".");
    const grouped = parsed.groupSeparator ? integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, parsed.groupSeparator) : integerPart;
    return decimalPart ? `${grouped}${parsed.decimalSeparator}${decimalPart}` : grouped;
}

interface Counter {
    span: HTMLSpanElement;
    parsed: ParsedNumber;
    finalText: string;
}

// Wraps every number in the element's text nodes in a span. Text nodes are collected first, and
// each one is split from its last match backwards, so splitting never shifts a match still to come.
function wrapNumbers(root: HTMLElement): { span: HTMLSpanElement; parsed: ParsedNumber }[] {
    const textNodes: Text[] = [];
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
        textNodes.push(node as Text);
    }

    const wrapped: { span: HTMLSpanElement; parsed: ParsedNumber }[] = [];
    const pattern = new RegExp(NUMBER_PATTERN.source, "g");
    for (const node of textNodes) {
        const matches = [...node.data.matchAll(pattern)];
        for (const match of matches.reverse()) {
            const numberNode = node.splitText(match.index);
            numberNode.splitText(match[0].length);
            const span = document.createElement("span");
            span.className = "count-up__number";
            numberNode.replaceWith(span);
            span.append(numberNode);
            wrapped.unshift({ span, parsed: parseNumber(match) });
        }
    }
    return wrapped;
}

function easeOutCubic(t: number): number {
    return 1 - (1 - t) ** 3;
}

// Starts the counter at its start value ahead of its entry (so the final value doesn't flash first), keeping
// the final width reserved so the suffix ("bn+") doesn't jitter while the digits change.
function reset(span: HTMLSpanElement, parsed: ParsedNumber, finalWidth: number): string {
    const finalText = span.textContent ?? "";
    span.style.display = "inline-block";
    span.style.minWidth = `${finalWidth}px`;
    span.textContent = formatNumber(startValue(parsed), parsed);
    return finalText;
}

function animate(counters: Counter[]): void {
    const start = performance.now();
    const step = (now: number): void => {
        const progress = Math.min((now - start) / DURATION_MS, 1);
        for (const { span, parsed, finalText } of counters) {
            const from = startValue(parsed);
            span.textContent = progress < 1 ? formatNumber(from + (parsed.value - from) * easeOutCubic(progress), parsed) : finalText;
            if (progress === 1) {
                span.style.removeProperty("min-width");
                span.style.removeProperty("display");
            }
        }
        if (progress < 1) {
            requestAnimationFrame(step);
        }
    };
    requestAnimationFrame(step);
}

export function initCountUp(): void {
    if (!("IntersectionObserver" in window) || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        return;
    }

    const counters = new Map<Element, Counter[]>();
    document.querySelectorAll<HTMLElement>("[data-count-up]").forEach((root) => {
        const rootCounters = wrapNumbers(root)
            .filter(({ parsed }) => parsed.value > 0)
            .map(({ span, parsed }) => ({ span, parsed, finalText: "" }));
        // Measure every final width before any number is reset, so one reset can't reflow another.
        const widths = rootCounters.map(({ span }) => span.getBoundingClientRect().width);
        rootCounters.forEach((counter, i) => {
            counter.finalText = reset(counter.span, counter.parsed, widths[i] ?? 0);
        });
        if (rootCounters.length > 0) {
            counters.set(root, rootCounters);
        }
    });
    if (counters.size === 0) {
        return;
    }

    const observer = new IntersectionObserver(
        (entries) => {
            for (const entry of entries) {
                const rootCounters = counters.get(entry.target);
                if (entry.isIntersecting && rootCounters) {
                    observer.unobserve(entry.target);
                    animate(rootCounters);
                }
            }
        },
        { threshold: THRESHOLD },
    );
    counters.forEach((_, root) => observer.observe(root));
}
